import React from "react";
import { Link } from "react-router-dom";
import { OUTBOUND_URL } from "../data/characters";
import { ArrowUpRight, BookOpen } from "lucide-react";

/**
 * Diagonal split character section. Alternates left/right layout.
 * Primary CTA + image link → OUTBOUND_URL (tracking).
 * Secondary CTA → /:id internal bio page.
 */
const CharacterSection = ({ character, index }) => {
  const reverse = index % 2 === 1;
  const sectionStyle = {
    "--split-primary": character.primary,
    "--split-secondary": character.secondary,
  };

  return (
    <section
      id={character.id}
      data-testid={`character-section-${character.id}`}
      className={`diag-split ${reverse ? "diag-split-reverse" : ""} relative w-full min-h-[90vh] flex items-center overflow-hidden`}
      style={sectionStyle}
      aria-labelledby={`char-${character.id}-name`}
    >
      <div
        className={`max-w-[1400px] mx-auto w-full px-6 sm:px-12 py-20 sm:py-28 grid grid-cols-1 md:grid-cols-2 gap-12 items-center ${reverse ? "md:[&>*:first-child]:order-2" : ""}`}
      >
        {/* Image side */}
        <div className="relative flex items-center justify-center">
          <a
            data-testid={`character-image-link-${character.id}`}
            href={OUTBOUND_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Chat with ${character.name}, ${character.archetype}`}
            className="block w-full"
          >
            <div className="relative aspect-[3/4] w-full max-w-[520px] mx-auto">
              <img
                src={character.image}
                alt={character.alt}
                loading={index < 2 ? "eager" : "lazy"}
                decoding="async"
                width="520"
                height="693"
                className="char-img absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </a>

          {/* Ticket logo watermark */}
          <div
            aria-hidden
            className={`hidden md:block absolute ${reverse ? "right-2" : "left-2"} top-4 ticket text-xs sm:text-sm`}
            style={{ "--split-secondary": character.secondary }}
          >
            CHARACTER
            <br />
            AI · CHAT
          </div>
        </div>

        {/* Text side */}
        <div
          className="relative z-10"
          style={{
            color: character.textOnSecondary || "#FFFFFF",
          }}
        >
          <span
            data-testid={`character-archetype-${character.id}`}
            className="inline-block text-xs sm:text-sm font-bold tracking-[0.25em] uppercase font-body opacity-80"
          >
            № 0{index + 1} — {character.archetype}
          </span>

          <h2
            id={`char-${character.id}-name`}
            data-testid={`character-name-${character.id}`}
            className="font-display mt-4 text-5xl sm:text-7xl lg:text-8xl font-black tracking-tighter uppercase leading-[0.9]"
          >
            {character.name}
          </h2>

          <p
            data-testid={`character-tagline-${character.id}`}
            className="font-display mt-4 text-xl sm:text-2xl font-bold opacity-95 max-w-xl"
          >
            {character.tagline}
          </p>

          <p
            data-testid={`character-description-${character.id}`}
            className="font-body mt-6 text-base sm:text-lg leading-relaxed max-w-xl opacity-90"
          >
            {character.description}
          </p>

          {/* Traits */}
          <ul
            data-testid={`character-traits-${character.id}`}
            className="mt-7 flex flex-wrap gap-2"
          >
            {character.traits.map((t) => (
              <li
                key={t}
                className="text-xs sm:text-sm font-bold tracking-wider uppercase border-2 border-current px-3 py-1.5"
              >
                {t}
              </li>
            ))}
          </ul>

          {/* CTAs: primary (tracking) + secondary (bio) */}
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <a
              data-testid={`character-chat-button-${character.id}`}
              href={OUTBOUND_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ "--shadow-col": character.primary }}
              className="char-cta inline-flex items-center gap-2 bg-black text-white font-display font-black uppercase tracking-tight text-base sm:text-lg px-7 py-4 border-2 border-black"
            >
              Chat with {character.name}
              <ArrowUpRight className="w-5 h-5" />
            </a>

            <Link
              data-testid={`character-bio-button-${character.id}`}
              to={`/${character.id}`}
              className="inline-flex items-center gap-2 bg-transparent font-display font-black uppercase tracking-tight text-base sm:text-lg px-6 py-4 border-2 border-current hover:bg-current transition group"
              style={{ color: character.textOnSecondary }}
            >
              <span className="group-hover:[color:var(--split-secondary)]">
                Read bio
              </span>
              <BookOpen className="w-5 h-5 group-hover:[color:var(--split-secondary)]" />
            </Link>
          </div>

          <span className="mt-4 inline-block text-xs font-bold tracking-[0.2em] uppercase opacity-80">
            Instant · Always on
          </span>
        </div>
      </div>
    </section>
  );
};

export default CharacterSection;
